import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import RecipeCard from './RecipeCard';
import { useGetAllFavoriteRecipes } from '../hooks/favoriteRecipe';
import { useAuth } from '../context/AuthContext';
import Masonry from 'react-masonry-css';

const RecipesForIngredientDrawer = ({ isOpen, onClose, ingredient }) => {
    const [recipes, setRecipes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [favoriteRecipes, setFavoriteRecipes] = useState([]);
    const { sessionToken } = useAuth();
    const { getAllFavoriteRecipes } = useGetAllFavoriteRecipes();

    const breakpointColumns = {
        default: 2,
        700: 1
    };

    // Fetch recipes when ingredient changes
    useEffect(() => {
        if (!isOpen || !ingredient || !sessionToken) {
            return;
        }

        const fetchRecipes = async () => {
            setLoading(true);
            try {
                const response = await fetch(`/api/v1/recipe-ingredient/ingredients/${ingredient.id}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${sessionToken}`
                    }
                });

                if (!response.ok) {
                    throw new Error(`Failed to fetch recipes: ${response.status}`);
                }

                const data = await response.json();
                if (Array.isArray(data)) {
                    setRecipes(data);
                } else {
                    setRecipes([]);
                }
            } catch (error) {
                console.error('Error fetching recipes:', error);
                setRecipes([]);
            } finally {
                setLoading(false);
            }
        };

        fetchRecipes();
    }, [ingredient, sessionToken, isOpen]);

    // Fetch favorite recipes
    useEffect(() => {
        if (!sessionToken || !isOpen) return;
        
        const fetchFavoriteRecipes = async () => {
            try {
                const data = await getAllFavoriteRecipes(sessionToken);
                if (Array.isArray(data)) {
                    setFavoriteRecipes(data);
                }
            } catch (error) {
                console.error('Error fetching favorites:', error);
            }
        };
        fetchFavoriteRecipes();
    }, [sessionToken, getAllFavoriteRecipes, isOpen]);

    return (
        <>
            {/* Overlay */}
            <div
                onClick={onClose}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 1040,
                    opacity: isOpen ? 1 : 0,
                    visibility: isOpen ? 'visible' : 'hidden',
                    transition: 'opacity 0.3s ease-in-out, visibility 0.3s ease-in-out'
                }}
            />

            {/* Drawer */}
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: '90%',
                    maxWidth: '700px',
                    backgroundColor: 'white',
                    boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.15)',
                    zIndex: 1050,
                    transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
                    transition: 'transform 0.3s ease-in-out',
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                {/* Header */}
                <div style={{
                    padding: '1.5rem',
                    borderBottom: '2px solid #dee2e6',
                    position: 'sticky',
                    top: 0,
                    backgroundColor: 'white',
                    zIndex: 10
                }}>
                    <div className="d-flex align-items-center">
                        <button
                            onClick={onClose}
                            style={{
                                background: 'none',
                                border: 'none',
                                fontSize: '2rem',
                                cursor: 'pointer',
                                padding: '0',
                                width: '40px',
                                height: '40px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                color: '#666',
                                lineHeight: 1,
                                marginRight: '1rem'
                            }}
                            aria-label="Close"
                        >
                            ×
                        </button>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <h4 className="mb-1" style={{ fontWeight: 'bold' }}>
                                Recipes with {ingredient?.name || 'Ingredient'}
                            </h4>
                            <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                                Discover recipes featuring this ingredient
                            </p>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div style={{ flex: 1, padding: '1.5rem' }}>
                    {loading ? (
                        <div className="text-center my-4">
                            <Spinner role="status" style={{ color: '#52cd92' }}>
                                <span className="visually-hidden">Loading recipes...</span>
                            </Spinner>
                            <p className="mt-3 text-muted">Finding recipes...</p>
                        </div>
                    ) : recipes.length === 0 ? (
                        <div className="text-center py-5">
                            <p className="text-muted mb-0">No recipes found with this ingredient.</p>
                        </div>
                    ) : (
                        <>
                            <h5 className="mb-3">
                                Found {recipes.length} recipe{recipes.length !== 1 ? 's' : ''}
                            </h5>
                            <Masonry
                                breakpointCols={breakpointColumns}
                                className="my-masonry-grid"
                                columnClassName="my-masonry-grid_column"
                            >
                                {recipes.map(recipe => (
                                    <RecipeCard 
                                        key={recipe.id} 
                                        recipe={recipe} 
                                        isFavorite={Array.isArray(favoriteRecipes) && favoriteRecipes.includes(Number(recipe.id))} 
                                    />
                                ))}
                            </Masonry>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default RecipesForIngredientDrawer;

